import React, { useState, useEffect } from 'react';
import { db } from '../../config/firebase';
import { collection, getDocs, updateDoc, doc, query, orderBy, onSnapshot, getDoc, deleteDoc, where } from 'firebase/firestore';
import './NotificationsPanel.css';
import './mobile.css';
import './BusinessDetailsModal.css';

const formatTimestamp = (timestamp) => {
  if (!timestamp) return 'Not specified';
  
  try {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    console.error('Error formatting timestamp:', error);
    return 'Invalid date';
  }
};

const NotificationsPanel = () => {
  const [notifications, setNotifications] = useState({
    business: [],
    advertising: [],
    updates: [],
    feedback: []
  });
  const [activeTab, setActiveTab] = useState('business');
  const [statusFilter, setStatusFilter] = useState('new');
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    show: false,
    notificationId: null,
    notificationType: null
  });
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        console.log('Fetching notifications...');
        
        const businessQuery = query(
          collection(db, 'businessRequests'),
          orderBy('timestamp', 'desc')
        );
        
        const advertisingQuery = query(
          collection(db, 'advertisingRequests'),
          orderBy('timestamp', 'desc')
        );
        
        const feedbackQuery = query(
          collection(db, 'feedback'),
          orderBy('timestamp', 'desc')
        );
        
        const businessUnsubscribe = onSnapshot(businessQuery, (snapshot) => {
          const allBusinessDocs = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            timestamp: doc.data().timestamp?.toDate()
          }));

          const businessList = allBusinessDocs.filter(doc => !doc.type || doc.type !== 'update');
          const updatesList = allBusinessDocs.filter(doc => doc.type === 'update');
          
          setNotifications(prev => ({
            ...prev,
            business: businessList,
            updates: updatesList
          }));
        });

        const advertisingUnsubscribe = onSnapshot(advertisingQuery, (snapshot) => {
          const advertisingList = snapshot.docs.map(doc => ({
            id: doc.id,
            type: 'advertising',
            ...doc.data(),
            timestamp: doc.data().timestamp?.toDate()
          }));
          
          setNotifications(prev => ({
            ...prev,
            advertising: advertisingList
          }));
        });

        const feedbackUnsubscribe = onSnapshot(feedbackQuery, (snapshot) => {
          console.log('Feedback snapshot:', snapshot.docs.map(doc => doc.data()));
          const feedbackList = snapshot.docs.map(doc => ({
            id: doc.id,
            type: 'feedback',
            ...doc.data(),
            timestamp: doc.data().timestamp?.toDate()
          }));
          
          console.log('Processed feedback list:', feedbackList);
          
          setNotifications(prev => ({
            ...prev,
            feedback: feedbackList
          }));
        });

        return () => {
          businessUnsubscribe();
          advertisingUnsubscribe();
          feedbackUnsubscribe();
        };
      } catch (error) {
        console.error('Error in feedback listener:', error);
      }
    };

    fetchNotifications();
  }, []);

  const handleAction = async (id, action, type) => {
    try {
      let collectionName;
      switch(type) {
        case 'feedback':
          collectionName = 'feedback';
          break;
        case 'business':
        case 'update':
          collectionName = 'businessRequests';
          break;
        default:
          collectionName = 'advertisingRequests';
      }
      
      const status = action === 'approved' ? 'reviewed' : 'archived';
      
      const updateData = {
        status: status,
        actionTimestamp: new Date(),
        actionBy: 'admin'
      };
      
      await updateDoc(doc(db, collectionName, id), updateData);

      console.log(`Request ${id} in ${collectionName} updated to ${status}`);
      
      if (detailsModalOpen) {
        handleCloseDetails();
      }

    } catch (error) {
      console.error('Error updating status:', error);
      alert('Error updating request status. Please try again.');
    }
  };

  const handleDeleteClick = (id, type) => {
    setDeleteConfirmation({
      show: true,
      notificationId: id,
      notificationType: type
    });
  };

  const handleConfirmDelete = async () => {
    try {
      const { notificationId, notificationType } = deleteConfirmation;
      let collectionName;
      
      switch(notificationType) {
        case 'feedback':
          collectionName = 'feedback';
          break;
        case 'business':
        case 'update':
          collectionName = 'businessRequests';
          break;
        case 'advertising':
          collectionName = 'advertisingRequests';
          break;
        default:
          throw new Error('Invalid notification type');
      }
      
      console.log(`Deleting ${notificationType} with ID: ${notificationId} from collection: ${collectionName}`);
      await deleteDoc(doc(db, collectionName, notificationId));
      
      setDeleteConfirmation({ show: false, notificationId: null, notificationType: null });
      console.log('Delete successful');
      
    } catch (error) {
      console.error('Error deleting notification:', error);
      alert('Error deleting notification. Please try again.');
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmation({ show: false, notificationId: null, notificationType: null });
  };

  const handleCleanupAll = async () => {
    if (window.confirm('Are you sure you want to delete ALL notifications? This cannot be undone.')) {
      try {
        const businessSnapshot = await getDocs(collection(db, 'businessRequests'));
        const businessPromises = businessSnapshot.docs.map(doc => 
          deleteDoc(doc.ref)
        );
        
        const advertisingSnapshot = await getDocs(collection(db, 'advertisingRequests'));
        const advertisingPromises = advertisingSnapshot.docs.map(doc => 
          deleteDoc(doc.ref)
        );

        await Promise.all([...businessPromises, ...advertisingPromises]);
        
        alert('All notifications have been cleared successfully!');
      } catch (error) {
        console.error('Error during cleanup:', error);
        alert('Error cleaning up notifications. Please try again.');
      }
    }
  };

  const handleViewDetails = (notification) => {
    setSelectedNotification(notification);
    setDetailsModalOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsModalOpen(false);
    setTimeout(() => {
      setSelectedNotification(null);
    }, 0);
  };

  const formatBusinessHours = (hours) => {
    if (!hours || typeof hours !== 'object') return 'Not specified';
    
    return Object.entries(hours).map(([day, schedule]) => {
      if (!schedule.isOpen) return `${day.charAt(0).toUpperCase() + day.slice(1)}: Closed`;
      return `${day.charAt(0).toUpperCase() + day.slice(1)}: ${schedule.open} - ${schedule.close}`;
    }).join('\n');
  };

  const formatTime = (time) => {
    if (!time) return 'Not specified';
    const [hours, minutes] = time.split(':');
    const period = hours >= 12 ? 'PM' : 'AM';
    const hour = hours % 12 || 12;
    return `${hour}:${minutes} ${period}`;
  };

  const renderDetailsModal = () => {
    if (!selectedNotification) return null;

    const handleTouchStart = (e) => {
      const touch = e.touches[0];
      const startY = touch.clientY;
      
      const handleTouchMove = (e) => {
      };
      
      document.addEventListener('touchmove', handleTouchMove, { passive: true });
      document.addEventListener('touchend', () => {
        document.removeEventListener('touchmove', handleTouchMove);
      }, { once: true });
    };

    const orderedDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

    return (
      <div className="details-modal-overlay">
        <div 
          className="details-modal"
          onTouchStart={handleTouchStart}
        >
          <div className="modal-content">
            <div className="left-column">
              <h4 className="section-header">BUSINESS INFORMATION</h4>
              <div className="info-section">
                <div className="detail-item">
                  <label>BUSINESS NAME</label>
                  <span>{selectedNotification.businessName}</span>
                </div>
                <div className="detail-item">
                  <label>CATEGORY</label>
                  <span>{selectedNotification.category}</span>
                </div>
                <div className="detail-item">
                  <label>DESCRIPTION</label>
                  <span>{selectedNotification.description || 'Not specified'}</span>
                </div>
              </div>

              <h4 className="section-header">CONTACT DETAILS</h4>
              <div className="info-section">
                <div className="detail-item">
                  <label>EMAIL</label>
                  <span>{selectedNotification.email}</span>
                </div>
                <div className="detail-item">
                  <label>PHONE</label>
                  <span>{selectedNotification.phone || selectedNotification.phoneNumber || 'Not specified'}</span>
                </div>
                <div className="detail-item">
                  <label>ADDRESS</label>
                  <span>{selectedNotification.address || 'Not specified'}</span>
                </div>
                <div className="detail-item">
                  <label>LOCATION/AREA</label>
                  <span>{selectedNotification.location || 'Not specified'}</span>
                </div>
              </div>
            </div>

            <div className="right-column">
              <h4 className="section-header">BUSINESS HOURS</h4>
              <div className="business-hours-container">
                {orderedDays.map(day => {
                  const schedule = selectedNotification.businessHours?.[day] || { isOpen: false };
                  return (
                    <div key={day} className="hours-day">
                      <span className="day-name">
                        {day.charAt(0).toUpperCase() + day.slice(1)}
                      </span>
                      <span className="hours">
                        {!schedule.isOpen ? 'Closed' : 
                          `${formatTime(schedule.open)} - ${formatTime(schedule.close)}`}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="modal-actions">
            <button className="back-btn" onClick={handleCloseDetails}>
              Back
            </button>
            <button 
              className="approve-btn"
              onClick={() => {
                handleAction(selectedNotification.id, 'approved', 'business');
                handleCloseDetails();
              }}
            >
              Approve
            </button>
            <button 
              className="deny-btn"
              onClick={() => {
                handleAction(selectedNotification.id, 'denied', 'business');
                handleCloseDetails();
              }}
            >
              Deny
            </button>
          </div>

          {window.innerWidth <= 768 && (
            <div className="scroll-indicator" />
          )}
        </div>
      </div>
    );
  };

  const renderNotificationCard = (notification) => {
    const renderDeleteButton = () => (
      <button 
        className="delete-icon-btn"
        onClick={() => handleDeleteClick(notification.id, notification.type)}
        title="Delete Request"
      >
        <i className="fas fa-trash"></i>
      </button>
    );

    if (notification.type === 'update') {
      return (
        <div key={notification.id} className="notification-card">
          <div className="card-header">
            <h3>{notification.businessName}</h3>
            {renderDeleteButton()}
          </div>
          <div className="notification-content">
            <div className="notification-field">
              <span className="field-label">Business Name</span>
              <span className="field-value">{notification.businessName}</span>
            </div>
            
            <div className="notification-field">
              <span className="field-label">Contact Person</span>
              <span className="field-value">{notification.fullName}</span>
            </div>
            
            <div className="notification-field">
              <span className="field-label">Email</span>
              <span className="field-value">{notification.email}</span>
            </div>
            
            <div className="notification-field">
              <span className="field-label">Phone</span>
              <span className="field-value">{notification.phone}</span>
            </div>
            
            <div className="notification-field">
              <span className="field-label">Update Type</span>
              <span className="field-value">{notification.updateType}</span>
            </div>
            
            <div className="notification-field">
              <span className="field-label">New Details</span>
              <span className="field-value">{notification.newDetails}</span>
            </div>
            
            <div className="notification-field">
              <span className="field-label">Reason for Update</span>
              <span className="field-value">{notification.updateReason}</span>
            </div>
            
            <div className="notification-field">
              <span className="field-label">Status</span>
              <span className={`status-badge ${(notification.status || 'pending').toLowerCase()}`}>
                {notification.status || 'pending'}
              </span>
            </div>
          </div>
          
          {(!notification.status || notification.status === 'pending') && (
            <div className="action-buttons">
              <button 
                className="approve-btn"
                onClick={() => handleAction(notification.id, 'approved', 'business')}
              >
                Approve
              </button>
              <button 
                className="deny-btn"
                onClick={() => handleAction(notification.id, 'denied', 'business')}
              >
                Deny
              </button>
            </div>
          )}
        </div>
      );
    } else if (notification.type === 'business') {
      return (
        <div key={notification.id} className="notification-card">
          <div className="card-header">
            <h3>{notification.businessName}</h3>
            {renderDeleteButton()}
          </div>
          <p><strong>Type:</strong> Business Registration</p>
          <p><strong>Category:</strong> {notification.category}</p>
          <p><strong>Status:</strong> {notification.status || 'pending'}</p>
          
          {(!notification.status || notification.status === 'pending') && (
            <div className="action-buttons">
              <button 
                className="view-details-btn"
                onClick={() => handleViewDetails(notification)}
              >
                <i className="fas fa-eye"></i> View Details
              </button>
            </div>
          )}
        </div>
      );
    } else if (notification.type === 'advertising') {
      return (
        <div key={notification.id} className="notification-card">
          <div className="card-header">
            <h3>{notification.companyName}</h3>
            {renderDeleteButton()}
          </div>
          <p><strong>Type:</strong> Advertising Request</p>
          <p><strong>Package:</strong> {notification.package}</p>
          <p><strong>Contact:</strong> {notification.contactPerson}</p>
          <p><strong>Email:</strong> {notification.email}</p>
          <p><strong>Phone:</strong> {notification.phone}</p>
          <p><strong>Status:</strong> {notification.status || 'pending'}</p>
          
          {(!notification.status || notification.status === 'pending') && (
            <div className="action-buttons">
              <button 
                className="approve-btn"
                onClick={() => handleAction(notification.id, 'approved', 'advertising')}
              >
                Approve
              </button>
              <button 
                className="deny-btn"
                onClick={() => handleAction(notification.id, 'denied', 'advertising')}
              >
                Deny
              </button>
            </div>
          )}
        </div>
      );
    }
    
    return null;
  };

  const getFilteredNotifications = () => {
    const currentNotifications = notifications[activeTab] || [];
    
    if (statusFilter === 'all') return currentNotifications;
    
    return currentNotifications.filter(notification => {
      console.log('Filtering notification:', notification.type, notification.status);
      
      switch (statusFilter) {
        case 'new':
          return !notification.status || notification.status === 'pending';
        case 'accepted':
          return notification.status === 'approved';
        case 'denied':
          return notification.status === 'denied';
        default:
          return true;
      }
    });
  };

  const renderFeedbackTab = () => {
    const filteredFeedback = notifications.feedback.filter(
      item => statusFilter === 'all' || item.status === statusFilter
    );

    return (
      <div className="notifications-list">
        {filteredFeedback.map(feedback => (
          <div key={feedback.id} className="notification-card">
            <div className="notification-header">
              <div className="feedback-type">
                <span className="type-label">{feedback.type}</span>
                <span className="timestamp">{formatTimestamp(feedback.timestamp)}</span>
              </div>
              <button 
                className="delete-btn"
                onClick={() => handleDeleteClick(feedback.id, 'feedback')}
                title="Delete feedback"
              >
                <i className="fas fa-trash"></i>
              </button>
            </div>

            <div className="notification-content">
              <p className="feedback-message">{feedback.message}</p>
              
              <div className="metadata-section">
                <small>
                  <i className="fas fa-user"></i> {feedback.userName || 'Anonymous'}
                </small>
                <small>
                  <i className="fas fa-globe"></i> {feedback.metadata?.platform || 'Unknown Platform'}
                </small>
                <small>
                  <i className="fas fa-language"></i> {feedback.metadata?.language || 'Unknown Language'}
                </small>
              </div>
            </div>

            <div className="notification-actions">
              <button
                className="action-btn review"
                onClick={() => handleAction(feedback.id, 'approved', 'feedback')}
              >
                Mark as Reviewed
              </button>
              <button
                className="action-btn archive"
                onClick={() => handleAction(feedback.id, 'archived', 'feedback')}
              >
                Archive
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="notifications-panel">
      <div className="notification-tabs">
        <button 
          className={`tab-button ${activeTab === 'business' ? 'active' : ''}`}
          onClick={() => setActiveTab('business')}
        >
          Business Registrations
        </button>
        <button 
          className={`tab-button ${activeTab === 'updates' ? 'active' : ''}`}
          onClick={() => setActiveTab('updates')}
        >
          Business Updates
        </button>
        <button 
          className={`tab-button ${activeTab === 'advertising' ? 'active' : ''}`}
          onClick={() => setActiveTab('advertising')}
        >
          Advertising Requests
        </button>
        <button 
          className={`tab-button ${activeTab === 'feedback' ? 'active' : ''}`}
          onClick={() => setActiveTab('feedback')}
        >
          Feedback
        </button>
      </div>

      <button 
        className="cleanup-btn"
        onClick={handleCleanupAll}
      >
        Clear All Notifications
      </button>

      <div className="status-filters">
        <button 
          className={`filter-btn all ${statusFilter === 'all' ? 'active' : ''}`}
          onClick={() => setStatusFilter('all')}
        >
          All
        </button>
        <button 
          className={`filter-btn new ${statusFilter === 'new' ? 'active' : ''}`}
          onClick={() => setStatusFilter('new')}
        >
          New
        </button>
        <button 
          className={`filter-btn accepted ${statusFilter === 'accepted' ? 'active' : ''}`}
          onClick={() => setStatusFilter('accepted')}
        >
          Accepted
        </button>
        <button 
          className={`filter-btn denied ${statusFilter === 'denied' ? 'active' : ''}`}
          onClick={() => setStatusFilter('denied')}
        >
          Denied
        </button>
      </div>

      <div className="notifications-list">
        {activeTab === 'business' && getFilteredNotifications().length === 0 ? (
          <div className="no-notifications">
            No business requests found
          </div>
        ) : (
          activeTab === 'business' && getFilteredNotifications().map(notification => renderNotificationCard(notification))
        )}
        {activeTab === 'advertising' && getFilteredNotifications().length === 0 ? (
          <div className="no-notifications">
            No advertising requests found
          </div>
        ) : (
          activeTab === 'advertising' && getFilteredNotifications().map(notification => renderNotificationCard(notification))
        )}
        {activeTab === 'updates' && getFilteredNotifications().length === 0 ? (
          <div className="no-notifications">
            No business updates found
          </div>
        ) : (
          activeTab === 'updates' && getFilteredNotifications().map(notification => renderNotificationCard(notification))
        )}
        {activeTab === 'feedback' && renderFeedbackTab()}
      </div>

      {deleteConfirmation.show && (
        <div className="confirm-modal-overlay">
          <div className="confirm-modal">
            <h4>Delete Notification</h4>
            <p>Are you sure you want to delete this notification?</p>
            <div className="confirm-modal-buttons">
              <button 
                className="cancel-delete-btn"
                onClick={handleCancelDelete}
              >
                Cancel
              </button>
              <button 
                className="confirm-delete-btn"
                onClick={handleConfirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      {detailsModalOpen && renderDetailsModal()}
    </div>
  );
};

export default NotificationsPanel; 